// Service ports and URLs
export const SERVICE_PORTS = {
  DENIAL_MANAGEMENT: {
    FRONTEND: 3154,
    BACKEND: 8154,
  },
  CLAIMS_PROCESSING: {
    FRONTEND: 3153,
    BACKEND: 8153,
  },
  CHARGE_CAPTURE: {
    FRONTEND: 3152,
    BACKEND: 8152,
  },
  INSURANCE_VERIFICATION: {
    FRONTEND: 3150,
    BACKEND: 8150,
  },
  PRIOR_AUTHORIZATION: {
    FRONTEND: 3151,
    BACKEND: 8151,
  },
  AR_POSTING: {
    FRONTEND: 3155,
    BACKEND: 8155,
  },
  REMITTANCE_ADVICE: {
    FRONTEND: 3157,
    BACKEND: 8157,
  },
  CONTRACT_MANAGEMENT: {
    FRONTEND: 3156,
    BACKEND: 8156,
  },
  RCM_COMPLIANCE: {
    FRONTEND: 3158,
    BACKEND: 8158,
  },
  EDI_PLATFORM: {
    FRONTEND: 3010,
    BACKEND: 8010,
  },
  PATIENT_ACCOUNT: {
    FRONTEND: 3110,
    BACKEND: 8110,
  },
  PATIENTS: {
    FRONTEND: 3100,
    BACKEND: 8100,
  },
  PRACTITIONERS: {
    FRONTEND: 3101,
    BACKEND: 8101,
  },
  CLAIMS_CORE: {
    FRONTEND: 3102,
    BACKEND: 8102,
  },
  TASK_MANAGER: {
    FRONTEND: 3051,
    BACKEND: 8051,
  },
  ACTIVITY_LOG: {
    FRONTEND: 3050,
    BACKEND: 8050,
  },
  /** Standalone Integrations UI (`Integrations/frontend`). BACKEND aligns with MCP default in Integrations/.env. */
  INTEGRATIONS_SERVICE: {
    FRONTEND: 3060,
    BACKEND: 8063,
  },
  MEDICAL_CODES: {
    FRONTEND: 3103,
    BACKEND: 8103,
  },
  ANALYTICS_FACADE: {
    BACKEND: 8056,
  },
  /** HealthCareFoundation/CoreServices/Payer — global + org payer catalog. */
  PAYER_SERVICE: {
    FRONTEND: 3109,
    BACKEND: 8109,
  },
};

/**
 * Base URL for an RCM microservice backend (e.g. http://host:8152).
 * Returns null when that service is skipped — callers must not call axios (avoids net::ERR_CONNECTION_REFUSED).
 *
 * Charge Management (8152):
 * - Explicit URL: REACT_APP_CHARGE_CAPTURE_API_URL=https://your-host/charge or http://host:8152
 * - Disable: REACT_APP_SKIP_CHARGE_CAPTURE=true or REACT_APP_CHARGE_CAPTURE_API_URL=skip
 * - Remote hosts (not localhost): by default do not assume :8152 is open. Set REACT_APP_USE_DEFAULT_RCM_PORTS=true
 *   to restore same-host :8152, or set REACT_APP_CHARGE_CAPTURE_API_URL.
 * Iframe UI (port 3152): set REACT_APP_CHARGE_CAPTURE_FRONTEND_URL, or rely on same-host derivation from
 * REACT_APP_CHARGE_CAPTURE_API_URL (hostname + :3152) when API URL is set.
 *
 * EDI Platform / clearinghouse (8010):
 * - Explicit URL: REACT_APP_EDI_PLATFORM_API_URL
 * - Disable: REACT_APP_SKIP_EDI_PLATFORM=true or REACT_APP_EDI_PLATFORM_API_URL=skip
 * - Remote hosts: same pattern as Charge Management — no assumed :8010 unless REACT_APP_USE_DEFAULT_RCM_PORTS=true
 *   or REACT_APP_EDI_PLATFORM_API_URL is set.
 *
 * RCM Compliance (8158):
 * - Explicit URL: REACT_APP_RCM_COMPLIANCE_API_URL=https://host/... (reverse proxy) — required for remote hosts when :8158 is not public.
 * - Disable: REACT_APP_SKIP_RCM_COMPLIANCE=true
 * - Remote: no default :8158 unless REACT_APP_USE_DEFAULT_RCM_PORTS=true
 *
 * AR Posting (8155):
 * - Explicit URL: REACT_APP_AR_POSTING_API_URL (origin or full .../api/v1 root; trailing /api/v1 is stripped for AgenticRCM axios base)
 * - Disable: REACT_APP_SKIP_AR_POSTING=true or URL skip/disabled/off/none
 * - Remote hosts: no assumed :8155 unless REACT_APP_USE_DEFAULT_RCM_PORTS=true or explicit URL (same pattern as Charge Management).
 *
 * Aggregated shell Work Queue (`shared/frontend/workqueue/rcmQueueAggregation.js`) uses the same
 * `REACT_APP_USE_DEFAULT_RCM_PORTS` rule plus optional `REACT_APP_AR_POSTING_API_URL`, `REACT_APP_CLAIMS_PROCESSING_API_URL`, etc.
 */
function normalizeAgenticRcmServiceBase(raw) {
  if (raw == null || raw === '') return null;
  let u = String(raw).trim().replace(/\/$/, '');
  if (u.endsWith('/api/v1')) {
    u = u.slice(0, -'/api/v1'.length).replace(/\/$/, '');
  }
  return u;
}

/** Use nginx /rcm-api/* proxies when running in AgenticRCM shell (port 3160) or remote hosts. */
function shouldUseRcmShellProxy() {
  if (process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true') return false;
  if (typeof window === 'undefined' || !window.location) return false;
  const { hostname, port } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocal && (port === '3000' || port === '3001')) return false;
  if (isLocal && port !== '3160' && port !== '80' && port !== '443' && port !== '') return false;
  return true;
}

export const getServiceApiUrl = (serviceName) => {
  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  const useDefaultPortsEverywhere = process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true';

  if (serviceName === 'DENIAL_MANAGEMENT') {
    const explicitRaw = process.env.REACT_APP_DENIAL_MANAGEMENT_API_URL;
    if (explicitRaw && /^https?:\/\//i.test(String(explicitRaw).trim())) {
      return normalizeAgenticRcmServiceBase(explicitRaw);
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/denial';
    }
  }

  if (serviceName === 'AR_POSTING') {
    const explicitRaw = process.env.REACT_APP_AR_POSTING_API_URL;
    if (explicitRaw && /^https?:\/\//i.test(String(explicitRaw).trim())) {
      return normalizeAgenticRcmServiceBase(explicitRaw);
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_AR_POSTING === 'true' ||
      (explicitRaw &&
        ['skip', 'disabled', 'off', 'none'].includes(String(explicitRaw).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/ar-posting';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'CLAIMS_PROCESSING') {
    const explicit = process.env.REACT_APP_CLAIMS_PROCESSING_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_CLAIMS_PROCESSING === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/claims-processing';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'INSURANCE_VERIFICATION') {
    const explicit = process.env.REACT_APP_INSURANCE_VERIFICATION_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_INSURANCE_VERIFICATION === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/insurance-verification';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'PRIOR_AUTHORIZATION') {
    const explicit = process.env.REACT_APP_PRIOR_AUTHORIZATION_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_PRIOR_AUTHORIZATION === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/prior-authorization';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'CHARGE_CAPTURE') {
    const explicit = process.env.REACT_APP_CHARGE_CAPTURE_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_CHARGE_CAPTURE === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/charge-capture';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'EDI_PLATFORM') {
    const explicit = process.env.REACT_APP_EDI_PLATFORM_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_EDI_PLATFORM === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/edi-platform';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'TASK_MANAGER') {
    const explicit = process.env.REACT_APP_TASK_MANAGER_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/task-manager';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'ACTIVITY_LOG') {
    const explicit = process.env.REACT_APP_ACTIVITY_LOG_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/activity-log';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'RCM_COMPLIANCE') {
    const explicit = process.env.REACT_APP_RCM_COMPLIANCE_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_RCM_COMPLIANCE === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/compliance';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'CONTRACT_MANAGEMENT') {
    const explicit = process.env.REACT_APP_CONTRACT_MANAGEMENT_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return normalizeAgenticRcmServiceBase(explicit);
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_CONTRACT_MANAGEMENT === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/contract-management';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'PAYER_SERVICE') {
    const explicit = process.env.REACT_APP_PAYER_SERVICE_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_PAYER_SERVICE === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (shouldUseRcmShellProxy()) {
      return '/rcm-api/payer';
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  if (serviceName === 'ANALYTICS_FACADE') {
    const explicit = process.env.REACT_APP_ANALYTICS_FACADE_API_URL;
    if (explicit && /^https?:\/\//i.test(String(explicit).trim())) {
      return String(explicit).trim().replace(/\/$/, '');
    }
    const explicitSkip =
      process.env.REACT_APP_SKIP_ANALYTICS_FACADE === 'true' ||
      (explicit && ['skip', 'disabled', 'off', 'none'].includes(String(explicit).trim().toLowerCase()));
    if (explicitSkip) {
      return null;
    }
    if (!isLocal && !useDefaultPortsEverywhere) {
      return null;
    }
  }

  const port = SERVICE_PORTS[serviceName]?.BACKEND || 8000;
  const protocol = typeof window !== 'undefined' && window.location ? window.location.protocol : 'http:';
  return `${protocol}//${hostname}:${port}`;
};

/**
 * Optional absolute base URL for embedded micro-app iframes (static UI ports), e.g. Charge Management on :3152.
 * ServiceFrame defaults to `${protocol}//${hostname}:${port}`; use this when only a reverse-proxy URL is public.
 * Keys = FRONTEND port from App.jsx SERVICE_PORTS (numbers).
 */
const IFRAME_FRONTEND_ENV_BY_PORT = {
  3152: process.env.REACT_APP_CHARGE_CAPTURE_FRONTEND_URL,
  3156: process.env.REACT_APP_CONTRACT_MANAGEMENT_FRONTEND_URL,
  3060: process.env.REACT_APP_INTEGRATIONS_FRONTEND_URL,
  3158: process.env.REACT_APP_RCM_COMPLIANCE_FRONTEND_URL,
  /** Patient / Practice Data — same pattern as AgenticPractice when only /services/* is public. */
  3100: process.env.REACT_APP_PATIENTS_FRONTEND_URL,
  3120: process.env.REACT_APP_PRACTICE_DATA_FRONTEND_URL,
  3103: process.env.REACT_APP_MEDICAL_CODES_FRONTEND_URL,
  3052: process.env.REACT_APP_DATA_IMPORTER_FRONTEND_URL,
};

export const getServiceFrontendIframeBase = (port) => {
  const n = Number(port);
  const raw = IFRAME_FRONTEND_ENV_BY_PORT[n];
  if (raw && String(raw).trim().match(/^https?:\/\//i)) {
    return String(raw).trim().replace(/\/$/, '');
  }
  return null;
};

// Navigation tabs
export const NAVIGATION_TABS = [
  { id: 'dashboard', label: 'Dashboard', path: '/', icon: 'LayoutDashboard' },
  { id: 'denials', label: 'Denials & Appeals', path: '/denials', icon: 'AlertTriangle' },
  { id: 'claims', label: 'Claims', path: '/claims', icon: 'FileText' },
  { id: 'charges', label: 'Charge Management', path: '/charges', icon: 'DollarSign' },
  { id: 'insurance', label: 'Insurance', path: '/insurance', icon: 'Shield' },
  { id: 'prior-auth', label: 'Prior Auth', path: '/prior-auth', icon: 'CheckCircle' },
  { id: 'work-queue', label: 'Work Queue', path: '/work-queue', icon: 'Inbox' },
  { id: 'analytics', label: 'Analytics', path: '/analytics', icon: 'BarChart' },
];

// Priority levels
export const PRIORITY_LEVELS = {
  URGENT: { value: 'urgent', label: 'Urgent', color: 'red' },
  HIGH: { value: 'high', label: 'High', color: 'orange' },
  MEDIUM: { value: 'medium', label: 'Medium', color: 'yellow' },
  LOW: { value: 'low', label: 'Low', color: 'green' },
};

// Status options
export const STATUS_OPTIONS = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  PENDING_REVIEW: 'pending_review',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

